import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Check, X as XIcon } from "lucide-react";
import { Input } from "@shadcn-in-nx/ui/input";
import Button from "../../../components/Button.jsx";
import Icon from "../../../components/Icon";
import { DialogNewWorkflow } from "../DialogNewWorkflow";

export default function WorkflowPageHeader({
    appId,
    currentApp,
    workflows,
    groupedWorkflows,
    viewMode,
    onUpdateAppName,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState("");

    const hasItems =
        (viewMode === "list" ? workflows.length : groupedWorkflows.length) > 0;

    const startEdit = () => {
        setEditing(true);
        setEditName(currentApp?.title || "");
    };

    const saveEdit = () => {
        if (editName.trim() && currentApp) {
            onUpdateAppName(currentApp.id, editName.trim());
        }
        setEditing(false);
    };

    const handleKey = (e) => {
        if (e.key === "Enter") saveEdit();
        if (e.key === "Escape") setEditing(false);
    };

    return (
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
                {appId && currentApp && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/workflows")}
                        className="flex items-center gap-2"
                    >
                        <Icon name="ArrowLeft" className="h-4 w-4" />
                        <span className="hidden sm:inline">
                            {t("workflowIndex.navigation.backToApps")}
                        </span>
                    </Button>
                )}
                {appId && currentApp ? (
                    editing ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={handleKey}
                                className="text-xl sm:text-2xl font-bold tracking-tight h-8 sm:h-10"
                                autoFocus
                            />
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={saveEdit}>
                                <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditing(false)}>
                                <XIcon className="h-4 w-4 text-red-600" />
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                                <span className="hidden sm:inline">
                                    {t("workflowIndex.appSpecific.appWorkflows", { appTitle: currentApp.title })}
                                </span>
                                <span className="sm:hidden">
                                    {t("workflowIndex.appSpecific.appWorkflowsMobile", { appTitle: currentApp.title })}
                                </span>
                            </h2>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={startEdit}>
                                <Edit2 className="h-3 w-3 text-gray-500" />
                            </Button>
                        </div>
                    )
                ) : (
                    <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                        {t("workflowIndex.pageTitle")}
                    </h2>
                )}
            </div>
            <div className="flex items-center gap-2">
                {(appId || hasItems) && (
                    <DialogNewWorkflow appType="AUTOMATION" appId={appId}>
                        <Button className="text-xs sm:text-sm" data-guided="new-workflow">
                            <Plus className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">
                                {appId && currentApp
                                    ? t("workflowIndex.appSpecific.newAutomationInApp", { appTitle: currentApp.title })
                                    : t("workflowIndex.newAutomationOrApp")}
                            </span>
                            <span className="sm:hidden">
                                {appId && currentApp
                                    ? t("workflowIndex.appSpecific.newInApp", { appTitle: currentApp.title })
                                    : t("workflowIndex.newMobile")}
                            </span>
                        </Button>
                    </DialogNewWorkflow>
                )}
            </div>
        </div>
    );
}
